// Module-scope helpers + constants shared across the MCM cooking
// pantry surface. Pulled out of KitchenScreen.jsx so the screen
// shell can stay focused on top-level wiring while the extracted
// component files (KitchenCard, ItemGrid, AddDraftSheet, …) all
// import a single canonical version of the helper.

import {
  findIngredient, hubForIngredient, getIngredientInfo,
  cutLabel, STATE_LABELS, unitLabel,
} from "../data/ingredients";
import { findFoodType } from "../data/foodTypes";
import { FRIDGE_TILES,  tileIdForItem        as fridgeTileFor  } from "./fridgeTiles";
import { PANTRY_TILES,  pantryTileIdForItem  as pantryTileFor  } from "./pantryTiles";
import { FREEZER_TILES, freezerTileIdForItem as freezerTileFor } from "./freezerTiles";
import { isBrandSentinel } from "./classifyScan";

// Default classifier helpers used when callers haven't threaded a
// live getInfo through. The classifier still works without getInfo —
// it just falls back to bundled heuristics. Surfaces that have access
// to useIngredientInfo() (anywhere in the React tree) SHOULD pass
// `getInfo` so DB-side `info.tile_routing` overrides fire too.
export const CLASSIFIER_HELPERS = { findIngredient, hubForIngredient };

// Build a helpers object that includes a live getInfo. Use this
// inside a React component that has called useIngredientInfo() —
// pass the result to classifyItem / toCard so AI-routed canonicals
// land on the tiles `scripts/ai_recategorize_misc.mjs` chose for
// them instead of falling back to the default heuristic.
export function makeClassifierHelpers(getInfo) {
  return { findIngredient, hubForIngredient, getInfo };
}

// The three locations in top-to-bottom kitchen order. Each carries
// the tile manifest + the classifier that maps items to tiles
// within it. Order here is the order the floating dock renders
// segments in. Emoji icons removed — the dock uses solid color
// swatch dots (LOCATION_DOT below) instead per the MCM direction:
// dark blue = fridge, orange = pantry, icy blue = freezer.
export const LOCATIONS = [
  { id: "fridge",  label: "Fridge",  tiles: FRIDGE_TILES,  classify: fridgeTileFor  },
  { id: "pantry",  label: "Pantry",  tiles: PANTRY_TILES,  classify: pantryTileFor  },
  { id: "freezer", label: "Freezer", tiles: FREEZER_TILES, classify: freezerTileFor },
];

// Fallback unit options for the package-size picker when no
// canonical is pinned (or the canonical declares no `units`
// array). Once a canonical is picked we switch to that
// canonical's own units so butter shows sticks / tbsp / cup
// instead of generic "count".
export const DEFAULT_UNIT_OPTIONS = [
  { id: "count", label: "count" },
  { id: "oz",    label: "oz" },
  { id: "lb",    label: "lb" },
  { id: "g",     label: "g" },
  { id: "kg",    label: "kg" },
  { id: "fl oz", label: "fl oz" },
  { id: "ml",    label: "ml" },
  { id: "L",     label: "L" },
  { id: "cup",   label: "cup" },
  { id: "tbsp",  label: "tbsp" },
  { id: "tsp",   label: "tsp" },
  { id: "pkg",   label: "pkg" },
  { id: "can",   label: "can" },
  { id: "jar",   label: "jar" },
  { id: "bag",   label: "bag" },
  { id: "box",   label: "box" },
  { id: "loaf",  label: "loaf" },
  { id: "stick", label: "stick" },
];

// Fallback mapping for Showcase demo items (they don't carry the
// real location / tileId axes). Keeps the design-reference
// surface useful — tapping into demo "dairy" lands in the Dairy
// & Eggs tile with its four demo cards, same grouping the
// production pantry does.
export const DEMO_CAT_MAP = {
  dairy:   { location: "fridge", tileId: "dairy"        },
  produce: { location: "fridge", tileId: "produce"      },
  meat:    { location: "fridge", tileId: "meat_poultry" },
  pantry:  { location: "pantry", tileId: "pasta_grains" },
};

// Pretty label for the STORED IN pill — maps Kitchen's category
// strings to the tile-ish names the MCM card pill expects. Real
// items carry `category` as dairy/produce/meat/pantry/etc. (see
// `usePantry.js` fromDb mapping). Demo items already have the
// human-readable string baked into `location`, so that path
// short-circuits.
export const CATEGORY_LABELS = {
  dairy:     "Dairy & Eggs",
  produce:   "Produce",
  meat:      "Meat & Seafood",
  pantry:    "Pantry",
  freezer:   "Freezer",
  drinks:    "Drinks",
  condiments:"Condiments",
  baking:    "Baking",
};

export const NAV_TABS = [
  { id: "pantry", label: "Pantry", glyph: "🥫" },
  { id: "cook",   label: "Cook",   glyph: "🍳" },
  { id: "plan",   label: "Plan",   glyph: "📅" },
  { id: "you",    label: "You",    glyph: "🌿" },
];

// Width of the swipe-reveal action drawer behind each item card
// (the Remove button). 96px gives the button comfortable tap
// area without consuming so much of the card width that a
// half-open swipe looks like a glitch.
export const SWIPE_ACTION_WIDTH = 96;

// Days between `date` and today, rounded. Negative → already
// expired. Null input → null (no chip shown). Uses midnight-to-
// midnight arithmetic so a card purchased today doesn't read
// "23h left" — it reads "3d" like the user expects.
export function daysUntil(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

// Format `amount + unit` into the same compact mono string the
// MCM demo items use ("1 stick", "8 large", "½ gallon"). The
// real pantry stores amount as a number; rendering 1.0 as "1"
// and 0.5 as "½" matches the intent without an extra dependency.
export function formatQty(amount, unit) {
  if (amount == null || Number.isNaN(amount)) return unit || "";
  const round = (n) => Math.round(n * 100) / 100;
  const a = round(amount);
  // Common fractional sugar — keep ½/¼/¾ as glyphs for the
  // recipe-book feel that matches the serif-italic name above.
  const FRAC = { 0.5: "½", 0.25: "¼", 0.75: "¾", 0.33: "⅓", 0.67: "⅔" };
  const frac = FRAC[a] || FRAC[round(a - Math.trunc(a))];
  const whole = Math.trunc(a);
  const display = frac
    ? (whole ? `${whole}${frac}` : frac)
    : a.toString();
  return unit ? `${display} ${unit}` : display;
}

// Classify a raw pantry row into a (location, tileId) pair. Tries
// the item's explicit placement first (row.location + row.tileId,
// set by Kitchen when the user dragged the card into a specific
// tile), falls back to the location-specific classifier that
// Kitchen itself uses so items without explicit placement land in
// the same tile the classic UI would put them in. Returns
// `{ location, tileId }` with sensible defaults when both paths
// give up ("pantry" / "misc").
export function classifyItem(raw, helpers = CLASSIFIER_HELPERS) {
  if (!raw) return { location: "pantry", tileId: "misc" };
  // Explicit user placement wins.
  const location = raw.location || defaultLocationForCategory(raw.category);
  if (raw.tileId) return { location, tileId: raw.tileId };
  // Otherwise re-run the classic Kitchen classifier for this location.
  const loc = LOCATIONS.find(l => l.id === location);
  if (!loc) return { location: "pantry", tileId: "misc" };
  const tileId = loc.classify(raw, helpers) || "misc";
  return { location, tileId };
}

// Coarse default — the app has a full `defaultLocationForCategory`
// helper elsewhere, but for the purposes of the tile grid we only
// need a best-guess when the row doesn't carry an explicit
// location. Matches the reserved-word defaults in CLAUDE.md.
// Looks up the canonical's freshness window for the given
// location, distinguishing sealed vs. opened state. The data
// lives in INGREDIENT_INFO (a separate map from the bundled
// INGREDIENTS array), so we go through getIngredientInfo()
// which merges the JS map, subcategory fallback, and any
// dbOverride from useIngredientInfo. Without this indirection
// findIngredient(id).storage is undefined for every bundled
// canonical and the auto-expiration silently dies.
//
// Pass an optional dbOverride (from useIngredientInfo's
// getInfo) so DB-approved enrichment overrides bundled.
// Preservation-grade STATES override the canonical's normal shelf
// life. USDA-aligned conservative defaults:
//   • jerky / cured / dried meats — ~1 year sealed at room temp,
//     ~14 days once opened (USDA jerky guidance).
//   • canned meats / fish / beans / tomatoes — ~3 years sealed,
//     ~3 days once opened (canned-goods baseline for low-acid retort
//     packs).
//   • shelf-stable dairy (uht / evaporated / condensed / powdered) —
//     ~12 months sealed in pantry, ~5 days once opened (and the user
//     should refrigerate it, hence the short opened window).
//   • dried fruit — ~6 months sealed pantry, ~30 days once opened.
//     Shorter than meat-jerky because dried fruit absorbs moisture
//     faster and goes rancid via residual oils.
//   • pickled / fermented — sealed jars are nearly indefinite (acid
//     environment); we cap at 24 months for sanity. Opened: 30 days
//     refrigerated.
// Admins can override per canonical via ingredient_info if a specific
// product wants tighter or looser windows. Returning null falls
// through to canonical storage data, so non-preservation states
// behave unchanged.
function shelfLifeForPreservedState(state, location, { opened = false } = {}) {
  if (state === "jerky" || state === "cured") {
    if (location !== "pantry") return null;
    return opened ? 14 : 365;
  }
  if (state === "dried") {
    // Differentiate meat-dried from produce-dried via the location
    // hint isn't possible here; default to fruit-dried numbers since
    // they're more conservative. Meat-dried (jerky/cured) callers
    // pass those states explicitly above.
    if (location !== "pantry") return null;
    return opened ? 30 : 180;
  }
  if (state === "canned") {
    if (!opened) {
      if (location !== "pantry") return null;
      return 365 * 3;
    }
    return 3;
  }
  if (state === "uht" || state === "evaporated" ||
      state === "condensed" || state === "powdered") {
    if (!opened) {
      if (location !== "pantry") return null;
      return 365;
    }
    return 5;
  }
  if (state === "pickled" || state === "fermented") {
    if (!opened) {
      if (location !== "pantry") return null;
      return 365 * 2;
    }
    return 30;
  }
  return null;
}

export function shelfLifeFor(canonicalId, location, { opened = false, dbOverride = null, state = null } = {}) {
  if (!canonicalId) return null;
  // STATE-axis override before canonical lookup so jerky/cured/dried
  // surfaces the right window even when the canonical's storage block
  // doesn't carry pantry shelf-life entries (chicken, beef, etc. only
  // have fridge / freezer numbers — pantry would otherwise return null
  // and the auto-expiration silently dies for shelf-stable forms).
  const fromState = shelfLifeForPreservedState(state, location, { opened });
  if (Number.isFinite(fromState)) return fromState;

  const ing = findIngredient(canonicalId);
  const info = getIngredientInfo(ing, dbOverride);
  const storage = info?.storage;
  if (!storage) return null;
  if (opened) {
    const op = storage.shelfLifeOpened;
    const days = op?.[location];
    return Number.isFinite(days) ? days : null;
  }
  const sealed = storage.shelfLife?.[location];
  if (Number.isFinite(sealed)) return sealed;
  const flat = storage.shelfLifeDays;
  return Number.isFinite(flat) ? flat : null;
}

// States whose physical container becomes a fridge candidate the
// moment the seal breaks. Canonical examples:
//   • canned tuna / SPAM — sealed retort pack is shelf-stable for
//     years; once opened, USDA says refrigerate within 2 hours and
//     consume in 3 days.
//   • UHT / evaporated / sweetened condensed milk — sealed brik /
//     can is shelf-stable; opened liquid milk needs fridge.
//   • pickles / sauerkraut / kimchi / olives — sealed jars hold
//     for years on the shelf, but opened the brine starts a slow
//     fermentation drift that's only paused at fridge temps.
//
// States explicitly EXCLUDED:
//   • powdered milk — resealable container in pantry is fine;
//     auto-fridging dry powder would surprise the user.
//   • jerky / cured / dried meat — opened bag stays in pantry as
//     long as it's kept dry.
//   • dried fruit — same as jerky; resealable bag in pantry.
export function needsFridgeAfterOpening(state) {
  return (
    state === "canned" ||
    state === "uht" ||
    state === "evaporated" ||
    state === "condensed" ||
    state === "pickled" ||
    state === "fermented"
  );
}

// Decide whether a sealed→opened transition should auto-migrate the
// row to the fridge. Returns the patch to apply, or null if no
// migration is warranted. Centralizing the predicate so every
// surface that mutates `amount` (gauge slider, consume / I-ate-this,
// cook-mode deduct, legacy ItemCard commit) routes through the same
// logic — the user sees identical behavior whether they bumped the
// gauge by hand or the cook flow drained the row past the seal.
//
// Returns:
//   • null              — no migration. Caller should not toast.
//   • { location, tileId } — apply this patch alongside the amount
//                            update. Caller should also fire a toast
//                            (with Undo) explaining what changed.
//
// Predicate: max>0, prev was sealed (>= max), new is opened (< max),
// state needs fridge after opening, currently in pantry. Anything
// else short-circuits to null.
export function autoMigratePatchOnOpen({ prevAmount, newAmount, max, state, location }) {
  const prev = Number(prevAmount);
  const next = Number(newAmount);
  const cap  = Number(max);
  if (!Number.isFinite(cap) || cap <= 0) return null;
  if (!Number.isFinite(prev) || !Number.isFinite(next)) return null;
  if (prev < cap) return null;            // already opened — not a transition
  if (next >= cap) return null;            // not actually crossing the seal
  if (!needsFridgeAfterOpening(state)) return null;
  if (location !== "pantry") return null;
  return { location: "fridge", tileId: "leftovers" };
}

// Human-readable noun for the auto-fridge toast message. Keeps the
// toast copy idiomatic ("opened pickles", "opened canned tuna")
// instead of leaking slug shorthand.
export function openedNounForState(state) {
  if (state === "canned")     return "canned goods";
  if (state === "uht")        return "UHT milk";
  if (state === "evaporated") return "evaporated milk";
  if (state === "condensed")  return "condensed milk";
  if (state === "pickled")    return "pickles";
  if (state === "fermented")  return "ferments";
  return state || "this";
}

export function defaultLocationForCategory(category) {
  if (!category) return "pantry";
  const c = String(category).toLowerCase();
  if (["dairy", "produce", "meat", "seafood", "drinks", "condiments", "herbs", "bread", "leftovers"].includes(c)) return "fridge";
  if (c.startsWith("frozen")) return "freezer";
  return "pantry";
}

// Coarse default — when the user picks Fridge / Pantry /
// Freezer in the add form, infer a category bucket so the
// classifier can tile the row. Mirrors defaultLocationForCategory
// in reverse (the inverse mapping). User can re-tile after the
// row lands via the existing edit flow.
export function defaultCategoryForLocation(location) {
  if (location === "freezer") return "frozen";
  if (location === "pantry")  return "pantry";
  return "dairy"; // most-likely fridge default; user re-tiles via edit
}

// Build the displayed item title from its identity components.
// CLAUDE.md identity-hierarchy rule: the HEADER is DERIVED, not
// free-text — `[Brand] [State] [Canonical] [Cut] ([Package size])`
// when those are set, falling back to canonical alone, falling back
// to raw.name only for free-text / pre-canonical rows. Package size
// uses raw.max (the size of the product AS SOLD) — amount < max means
// partial-remaining, but the package's identity is still its sold
// size. Including state, cut, and package size here so a row like
// (brand="Slim Jim", state="ground", canonical=chicken, max=12,
// unit="oz") reads as "Slim Jim Ground Chicken (12 oz)" instead of
// whatever long marketing string the user happened to scan.
//
// Empty pieces are skipped, so a brand-less ground-chuck row
// reads "Ground Beef Chuck" without leading whitespace, and a
// row that's only a canonical reads as just the canonical name.
export function buildDisplayName(raw) {
  // Backed by the parts builder so the plain-string callers and
  // the segmented renderers stay in lockstep.
  return buildDisplayNameParts(raw).map(p => p.text).join(" ");
}

// Same composition rule as buildDisplayName but returns segments
// tagged by axis kind so renderers can style each piece differently
// (e.g. dim the brand, emphasize the canonical). Each segment is
// `{ kind, text }`. `kind` is one of:
//   "brand"        — manufacturer label
//   "state"        — physical form (sliced, ground, …)
//   "canonical"    — the canonical identity ("Cheddar", "Salami", …)
//                    THIS is the load-bearing word and renderers
//                    typically hold it at full weight.
//   "cut"          — anatomical slot (breast, ribeye, …)
//   "packageSize"  — "(16 oz)" suffix; rendered in parens by the
//                    builder so `text` already contains the punctuation.
//   "freeText"     — no canonical resolved; raw.name fallback.
export function buildDisplayNameParts(raw) {
  if (!raw) return [{ kind: "freeText", text: "Untitled" }];
  const ing = raw.canonicalId ? findIngredient(raw.canonicalId) : null;
  if (!ing) {
    return [{ kind: "freeText", text: raw.name?.trim() || "Untitled" }];
  }
  const parts = [];
  // Brand sentinel ("Fresh Produce" / "Fresh Seafood") is a tier
  // helper, not a real brand — skip it in the title composition so
  // the kitchen card reads "Kale" instead of "Fresh Produce Kale".
  // Real brands ride along verbatim. isBrandSentinel keeps the
  // membership test centralized so tweaks to the sentinel set don't
  // need a parallel edit here.
  if (raw.brand?.trim() && !isBrandSentinel(raw.brand.trim())) {
    parts.push({ kind: "brand", text: raw.brand.trim() });
  }
  if (raw.state) {
    const txt = STATE_LABELS[raw.state] || raw.state;
    if (txt) {
      // Title-case the state segment so it matches the
      // already-title-cased canonical (`sliced` → `Sliced`,
      // `steak cut` → `Steak Cut`). STATE_LABELS values are stored
      // lowercase by convention; capitalization belongs in the
      // composer for display-string consumers.
      const cased = txt.replace(/\b\w/g, (c) => c.toUpperCase());
      parts.push({ kind: "state", text: cased });
    }
  }
  if (ing.name) {
    // Brand/canonical token-overlap dedupe — when the brand already
    // ENDS with the canonical's word(s), repeating them reads as a
    // stutter: brand "Certified Angus Beef" + canonical "Beef" used
    // to compose "Certified Angus Beef Beef". Drop the leading
    // canonical tokens the brand tail already carries; if the brand
    // swallows the WHOLE canonical name, skip the canonical segment
    // (the brand text still names the identity). Word-level compare,
    // case-insensitive, so "Beefy Boys" + "Beef" does NOT collapse
    // ("beefy" ≠ "beef").
    let canonicalText = ing.name;
    const brandPart = parts.find((p) => p.kind === "brand");
    if (brandPart) {
      const brandWords = brandPart.text.toLowerCase().split(/\s+/);
      const canonWords = ing.name.split(/\s+/);
      // Longest k where the brand's last k words equal the
      // canonical's first k words (case-insensitive).
      const maxK = Math.min(brandWords.length, canonWords.length);
      let overlap = 0;
      for (let k = maxK; k > 0; k -= 1) {
        const brandTail = brandWords.slice(brandWords.length - k);
        const canonHead = canonWords.slice(0, k).map((w) => w.toLowerCase());
        if (brandTail.every((w, i) => w === canonHead[i])) {
          overlap = k;
          break;
        }
      }
      canonicalText = canonWords.slice(overlap).join(" ");
    }
    if (canonicalText) parts.push({ kind: "canonical", text: canonicalText });
  }
  if (raw.cut) {
    const txt = cutLabel(raw.cut);
    if (txt) parts.push({ kind: "cut", text: txt });
  }
  if (parts.length === 0) {
    return [{ kind: "freeText", text: raw.name?.trim() || "Untitled" }];
  }
  // Suffix shows the AS-SOLD size (raw.max), NOT the current
  // remaining quantity (raw.amount). A half-eaten 16 oz tub still
  // reads "(16 oz)" — identity is what you bought; the fill gauge on
  // the card carries how much is left. Rows without a max (legacy or
  // quantityless) fall back to amount so the suffix doesn't vanish.
  const maxN = Number(raw.max);
  const amtN = Number.isFinite(maxN) && maxN > 0 ? maxN : Number(raw.amount);
  if (Number.isFinite(amtN) && amtN > 0 && raw.unit) {
    const ul = unitLabel(ing, raw.unit);
    // Trim trailing zeros from decimals (8.0 → 8, 8.5 → 8.5)
    const display = Number.isInteger(amtN)
      ? String(amtN)
      : amtN.toFixed(1).replace(/\.0$/, "");
    parts.push({ kind: "packageSize", text: `(${display} ${ul})` });
  }
  return parts;
}

// Pull display claims off a raw pantry row. Source order:
//   1. raw.attributes.claims  (legacy ItemCard write path —
//      the curated user-facing claim list)
//   2. raw.claims             (newer scan-write shape)
//   3. raw.scanDebug.memoryBook.claims (AI-photo passthrough)
// Returns a deduped string array; empty when nothing surfaces.
export function getItemClaims(raw) {
  if (!raw) return [];
  const candidates = [];
  if (Array.isArray(raw.attributes?.claims)) candidates.push(...raw.attributes.claims);
  if (Array.isArray(raw.claims)) candidates.push(...raw.claims);
  const mb = raw.scanDebug?.memoryBook?.claims;
  if (Array.isArray(mb)) candidates.push(...mb);
  // Dedupe while preserving order.
  const seen = new Set();
  const out = [];
  for (const c of candidates) {
    const t = typeof c === "string" ? c.trim() : "";
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

// Map a raw pantry row (from usePantry) OR a demo row into the
// card shape the KitchenCard renderer expects. Detects by whether
// `.qty` is pre-baked (demo) vs. derived from `.amount` / `.unit`
// (real). Keeps the downstream card component dumb — it sees
// ONE shape regardless of source. Also stamps `_location` +
// `_tileId` so the tile-first grouping layer can bucket the card
// without re-classifying on every render.
export function toCard(raw, helpers = CLASSIFIER_HELPERS) {
  if (raw == null) return null;
  // Demo row — already in card shape. Stamp a synthetic
  // (location, tileId) so Showcase's tile view looks populated.
  if (typeof raw.qty === "string" && typeof raw.cat === "string") {
    const fallback = DEMO_CAT_MAP[raw.cat] || { location: "pantry", tileId: "misc" };
    return { ...raw, _location: fallback.location, _tileId: fallback.tileId };
  }
  // Real pantry row — derive card fields + classify into tile.
  const { location: _location, tileId: _tileId } = classifyItem(raw, helpers);
  const days = daysUntil(raw.expiresAt);
  const status = days != null && days <= 3 ? "warn" : "ok";
  const cat = raw.category || "pantry";
  const location = CATEGORY_LABELS[cat] || (cat[0]?.toUpperCase() + cat.slice(1)) || "Pantry";
  // Food-type label for the per-item category pill. Resolves the
  // typeId (WWEIA wweia_cheese / wweia_yogurt / etc.) to its
  // human label ("Cheese", "Yogurt"). Falls back to the broad
  // category ("Dairy") when the item has no typeId set; null
  // when even that's missing so the pill simply hides.
  const foodTypeLabel = findFoodType(raw.typeId)?.label
    || (raw.category ? raw.category[0].toUpperCase() + raw.category.slice(1) : null);
  return {
    id:       raw.id,
    emoji:    raw.emoji || "🍽️",
    // Display name is derived from identity components per the
    // CLAUDE.md hierarchy rule, so a typo'd raw.name doesn't
    // fossilize as the visible title once the canonical lands.
    name:     buildDisplayName(raw),
    rawName:  raw.name || null,
    cut:      raw.cut || null,
    state:    raw.state || null,
    claims:   getItemClaims(raw),
    qty:      formatQty(raw.amount, raw.unit),
    // Raw amount + max for the fill gauge — mirrors classic
    // Kitchen's pct() helper. Cards use these to render the
    // sealed/opened bar; `qty` keeps the human-friendly
    // formatted string for the subheader row.
    amount:   raw.amount != null ? Number(raw.amount) : null,
    max:      raw.max    != null ? Number(raw.max)    : null,
    unit:     raw.unit   || null,
    // Canonical id flows through so KitchenCard can read the
    // canonical's nutrition data via useIngredientInfo / the
    // bundled INGREDIENTS registry. Lets the card surface
    // calories without re-running classification.
    canonicalId: raw.canonicalId || null,
    brand:    raw.brand || null,
    location,
    cat,
    status,
    days,
    typeLabel: foodTypeLabel,
    // Timestamps used for sort orderings in the drilled view.
    // `purchasedAt` may be null for manual-add rows that never
    // went through the scan flow; sort falls back to createdAt
    // semantics when present.
    purchasedAt: raw.purchasedAt || null,
    // Keep the raw row around so onOpenItem can hand it back to
    // the caller (App.jsx wants to open ItemCard with the full
    // pantry row, not the trimmed card shape).
    _raw: raw,
    _location,
    _tileId,
  };
}

// Sum all items across a location's tiles. `locBuckets` is
// `{ tileId: card[] }` or undefined; returns 0 for undefined so
// empty locations drop out of the breakdown naturally.
export function sumLocationTiles(locBuckets) {
  if (!locBuckets) return 0;
  let n = 0;
  for (const tileId of Object.keys(locBuckets)) n += locBuckets[tileId].length;
  return n;
}

// Sort a list of card-shape items by one of three orderings.
// "expiring"  — ascending days-to-expire, with null-days (shelf
//               stable) and missing dates bucketed at the end.
//               Warn items cluster at the top of the view.
// "name"      — case-insensitive alpha by display name.
// "recent"    — descending purchasedAt; rows without a stamp
//               (manual-add legacy) bucket at the end.
export function sortItems(items, sortBy) {
  const arr = items.slice();
  if (sortBy === "name") {
    arr.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return arr;
  }
  if (sortBy === "recent") {
    arr.sort((a, b) => {
      const ta = a.purchasedAt ? +new Date(a.purchasedAt) : 0;
      const tb = b.purchasedAt ? +new Date(b.purchasedAt) : 0;
      return tb - ta;
    });
    return arr;
  }
  // Default — "expiring" — days ascending, nulls last.
  arr.sort((a, b) => {
    const da = a.days == null ? Number.POSITIVE_INFINITY : a.days;
    const db = b.days == null ? Number.POSITIVE_INFINITY : b.days;
    return da - db;
  });
  return arr;
}

// Given a list of cards, pick the single most urgent warn item.
// Tie-breaker: the earliest expiry date (smallest days), then the
// first in pantry order. Null when no cards warn.
export function firstExpiring(cards) {
  let best = null;
  for (const c of cards) {
    if (c.status !== "warn") continue;
    if (best == null) { best = c; continue; }
    const bd = best.days == null ? Number.POSITIVE_INFINITY : best.days;
    const cd = c.days    == null ? Number.POSITIVE_INFINITY : c.days;
    if (cd < bd) best = c;
  }
  return best;
}

// Compact chip for the days-to-expire corner. Demo items always
// carry a number; real items whose `expiresAt` is null (shelf-
// stable pantry goods like olive oil) get a `days = null` from
// the adapter and render with an empty chip so the card doesn't
// lie about a spoilage clock.
export function formatDaysChip(days) {
  if (days == null) return "";
  if (days < 0) return "gone";
  if (days === 0) return "today";
  return `${days}d`;
}

// True when the item was purchased in the last 24 hours — used
// to flag rows with a small "NEW" chip on the card so recent
// grocery runs are visible at a glance. Demo rows and manual
// adds without a purchasedAt stamp return false (the chip
// wouldn't be meaningful for them).
export function isRecent(item) {
  const p = item?.purchasedAt;
  if (!p) return false;
  const d = p instanceof Date ? p : new Date(p);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) < 24 * 3600 * 1000;
}

// Urgency-tiered color for the days chip. Thresholds match the
// warn-card threshold (≤3) so the chip color and the card's
// warn wash flip on the same row. Plan-ahead window is 4–7 days;
// past that the chip fades back to the normal muted ink so
// long-life items don't perpetually draw attention. Null days
// (shelf-stable) use the muted default — no date, no urgency.
export function daysChipColor(days, theme) {
  if (days == null) return theme.color.inkMuted;
  if (days < 0 || days <= 3) return theme.color.burnt;     // warn
  if (days <= 7) return theme.color.mustard;               // plan-ahead
  return theme.color.inkMuted;                             // plenty
}
